import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as Sentry from '@sentry/react';
import type { PublicUser } from '@evi/shared';
import { clearExplicitSignOut } from '@/lib/sessionRestore';

// Sentry user context — ID only, no PII. Audit HIGH 1.2.
function syncSentryUser(user: PublicUser | null): void {
  if (user) {
    Sentry.setUser({ id: user._id });
  } else {
    Sentry.setUser(null);
  }
}

interface AuthState {
  user: PublicUser | null;
  accessToken: string | null;
  /** Derived — true when user is set. Do not set directly. */
  isAuthenticated: boolean;
  setAuth: (user: PublicUser, accessToken: string) => void;
  clearAuth: () => void;
  setAccessToken: (token: string) => void;
  /** Phase 7g.4b.4 Commit 2 — optimistically merge fields into the
   *  persisted PublicUser without firing a /auth/me refetch. Used by
   *  surfaces that just wrote the same fields to the server and want
   *  the auth store to reflect the new values immediately (TopNav
   *  UserChip, etc.). No-op when user is null. Generic shape so
   *  future surfaces can reuse the same action for any subset of
   *  PublicUser fields; PublicUser does not include phone, so only
   *  fields that live on the auth store can flow through this action. */
  updateUser: (patch: Partial<PublicUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setAuth: (user, accessToken) => {
        syncSentryUser(user);
        // A sign-in supersedes this tab's earlier sign-out, so a later
        // startup may restore again.
        clearExplicitSignOut();
        set({ user, accessToken, isAuthenticated: true });
      },

      clearAuth: () => {
        syncSentryUser(null);
        set({ user: null, accessToken: null, isAuthenticated: false });
      },

      setAccessToken: (token) =>
        set({ accessToken: token }),

      updateUser: (patch) =>
        set((state) => {
          if (!state.user) return {};
          const next = { ...state.user, ...patch };
          syncSentryUser(next);
          return { user: next };
        }),
    }),
    {
      name: 'evi-auth',
      // Persist user + accessToken so page refreshes don't lose auth.
      // accessToken lives 1 h. On expiry the 30-day refresh cookie rotates
      // it via the afterResponse hook in api.ts, and when this saved state
      // is gone useBootstrapAuth restores the session from that cookie.
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
